"use client"

import LinkButton from "components/Buttons/Link"
import ChevronSVG from "images/Icons/Chevron.inline.svg"
import CardSVG from "images/InsuranceCard.inline.svg"
import American from "images/home/testimonials/American.svg"
import Anthem from "images/home/testimonials/Anthem.svg"
import Artis from "images/home/testimonials/Artis.svg"
import Testimonial1IMG from "images/home/testimonials/Testimonial1.png"
import Testimonial1ProfileIMG from "images/home/testimonials/Testimonial1Profile.png"
import Testimonial2IMG from "images/home/testimonials/Testimonial2.png"
import Testimonial2ProfileIMG from "images/home/testimonials/Testimonial2Profile.png"
import Testimonial3IMG from "images/home/testimonials/Testimonial3.png"
import Testimonial3ProfileIMG from "images/home/testimonials/Testimonial3Profile.png"
import { InfiniteSideScroll } from "library/InfiniteSideScroll"
import StaticImage from "library/StaticImage"
import UniversalLink from "library/link"
import { css, f, styled } from "library/styled"
import { useMedia } from "library/useMedia"
import { useMemo, useState } from "react"
import colors from "styles/colors"
import textStyles from "styles/text"
import links from "utils/links"

export default function Testimonials() {
	const [activeIndex, setActiveIndex] = useState<number>(0)
	const marqueeSpeed = useMedia(0.4, 0.4, 0.3, 0.2)
	const marqueeItems = [
		"day cares",
		"auto shops",
		"tech startups",
		"design agencies",
		"accounting firms",
		"bakeries",
		"financial institutions",
		"retail",
		"restaurants",
	]
	const linkSize = useMedia("large", "large", "large", "medium")
	const mobile = useMedia(false, false, false, true)

	const TESTIMONIAL_DATA = useMemo(
		() => [
			{
				name: "Heaven Gomez",
				jobTitle: "Broker",
				companyLogo: Anthem,
				profileImage: Testimonial1ProfileIMG,
				testimonialImage: Testimonial1IMG,
				quote:
					"With Arlo, I can deliver firm quotes in minutes and promise clients rate stability—something the big carriers can’t match. With their concierge support, my phone stops ringing for every claim hiccup, and my book of business keeps growing.",
				colors: {
					primary: colors.green200,
					secondary: colors.green300,
					chevronFill: colors.green100,
					chevronStroke: colors.green400,
				},
			},
			{
				name: "Jennifer Allred",
				jobTitle: "Owner",
				companyLogo: Artis,
				profileImage: Testimonial2ProfileIMG,
				testimonialImage: Testimonial2IMG,
				quote:
					"Switching to Arlo cut our renewal increase by half and took a mountain of benefits questions off my HR team’s plate. Employees love the $0 primary-care visits, and I finally have transparent cost data to forecast next year’s budget with confidence.",
				colors: {
					primary: colors.purple300,
					secondary: colors.purple200,
					chevronFill: colors.purple100,
					chevronStroke: colors.purple300,
				},
			},
			{
				name: "Jackson Powell",
				jobTitle: "Member",
				companyLogo: American,
				profileImage: Testimonial3ProfileIMG,
				testimonialImage: Testimonial3IMG,
				quote:
					"I used to avoid the doctor because I never knew what the bill would be. With Arlo, my PCP visits are $0 and every procedure comes with a price tag up front. I’ve already done my annual physical and a cholesterol test—no surprises.",
				colors: {
					primary: colors.blue400,
					secondary: colors.blue400,
					chevronFill: colors.blue100,
					chevronStroke: colors.blue400,
				},
			},
		],
		[],
	)

	return (
		<Wrapper>
			<Inner>
				<Top>
					<Title>Trusted by businesses, built for affordability.</Title>
					<Subcontent>
						<Subtitle>
							From factories to startups, businesses trust Arlo for quality,
							affordable healthcare. Join hundreds of employers choosing a
							smarter way to offer benefits.
						</Subtitle>
						<LinkButton href={links.contact} size={linkSize} variant="lightAlt">
							Get Started Today
						</LinkButton>
					</Subcontent>
				</Top>
				<TestimonialsWrapper>
					{TESTIMONIAL_DATA.map((testimonial, index) => {
						const {
							colors: { primary, secondary, chevronFill, chevronStroke },
							name,
							jobTitle,
							companyLogo,
							profileImage,
							testimonialImage,
							quote,
						} = testimonial
						const isOpen = mobile ? true : activeIndex === index
						return (
							<TestimonialWrapper
								key={name}
								isOpen={isOpen}
								tabIndex={0}
								onKeyDown={(e: React.KeyboardEvent) => {
									if (e.key === "ArrowRight") {
										e.preventDefault()

										const currentElement = e.target as HTMLElement
										const nextWrapper =
											currentElement.nextElementSibling as HTMLElement
										if (nextWrapper) nextWrapper.focus()
									}
									if (e.key === "ArrowLeft") {
										e.preventDefault()
										const currentElement = e.target as HTMLElement
										const prevWrapper =
											currentElement.previousElementSibling as HTMLElement
										if (prevWrapper) prevWrapper.focus()
									}
									if (e.key === "Enter" || e.key === " ") {
										e.preventDefault()
										setActiveIndex(index)
									}
								}}
							>
								<Testimonial
									type="button"
									onClick={() => setActiveIndex(index)}
									isOpen={isOpen}
									onKeyDown={(e) => {
										if (e.key === "Enter" || e.key === " ") {
											e.preventDefault()
											setActiveIndex(index)
										}
									}}
									color={primary}
									aria-label="expand testimonial to view quote"
									aria-expanded={activeIndex === index}
								>
									{/* TODO - ADD BAck in when needed */}
									{/* <CompanyLogo
										isOpen={isOpen}
										src={companyLogo}
										width={63}
										height={14}
										alt=""
									/> */}
									<Chevron
										colors={{ primary: chevronStroke, secondary: chevronFill }}
										isOpen={isOpen}
										aria-hidden="true"
									/>
									<Author isOpen={isOpen}>
										<AuthorImage
											src={profileImage}
											width={32}
											height={32}
											alt=""
										/>
										<AuthorInfo>
											<AuthorName color={primary}>{name}</AuthorName>
											<JobTitle color={secondary}>{jobTitle}</JobTitle>
										</AuthorInfo>
									</Author>
									<ImageWrapper isOpen={isOpen}>
										<TestimonialImage
											src={testimonialImage}
											width={320}
											height={289}
											alt=""
										/>
									</ImageWrapper>
								</Testimonial>
								<Quote color={secondary} isOpen={isOpen}>
									<QuoteInner>{quote}</QuoteInner>
								</Quote>
							</TestimonialWrapper>
						)
					})}
				</TestimonialsWrapper>
				<MarqueeWrapper>
					<Marquee marqueeSpeed={marqueeSpeed} disableDrag>
						{marqueeItems.map((item) => (
							<MarqueeItem key={item}>
								<MarqueeText>{item}</MarqueeText>
								<CardIcon />
							</MarqueeItem>
						))}
					</Marquee>
				</MarqueeWrapper>
			</Inner>
		</Wrapper>
	)
}

const Wrapper = styled("section", {
	...f.responsive(css`
		display: grid;
		place-items: center;
		background: ${colors.offWhite};
		position: relative;
		z-index: 2;
	`),
	...f.tablet(css`
		padding-bottom: 32px;
	`),
})

const Inner = styled("div", {
	...f.unresponsive(css`
		border: 0.75px solid ${colors.green100};
		border-radius: 16px 16px 0 0;
	`),
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		width: 1376px;
		margin: 0 auto;
		overflow: clip;
	`),
	...f.tablet(css`
		width: 960px;
		gap: 0;
	`),

	...f.mobile(css`
		width: 327px;
		gap: 0;
	`),
})

const Top = styled("div", {
	...f.unresponsive(css`
		border-bottom: 0.75px solid ${colors.green100};
	`),
	...f.responsive(css`
		display: flex;
		justify-content: space-between;
		padding: 81px 32px 125px;
	`),
	...f.tablet(css`
		flex-direction: column;
		justify-content: space-between;
		padding: 42px;
		gap: 56px;
	`),

	...f.mobile(css`
		flex-direction: column;
		justify-content: space-between;
		padding: 40px 20px;
		gap: 64px;
	`),
})

const Title = styled("h2", {
	...f.responsive(css`
		width: 611px;
		color: ${colors.green500};
		${textStyles.h4};
	`),

	...f.mobile(css`
		width: 100%;
		${textStyles.h6};
	`),
})

const Subcontent = styled("div", {
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		gap: 36px;
		width: 483px;
	`),

	...f.mobile(css`
		width: 100%;
	`),
})

const Subtitle = styled("p", {
	...f.responsive(css`
		color: ${colors.green400};
		${textStyles.bodyR};
		margin-left: 5px;
	`),

	...f.mobile(css`
		${textStyles.bodyS};
	`),
})

const TestimonialsWrapper = styled("div", {
	...f.responsive(css`
		height: 720px;
		display: flex;
	`),
	...f.tablet(css`
		height: auto;
		flex-direction: column;
	`),

	...f.mobile(css`
		height: auto;
		flex-direction: column;
	`),
})

const TestimonialWrapper = styled("div", ({ isOpen }: { isOpen?: boolean }) => {
	return {
		...f.responsive(css`
			position: relative;
			padding: 20px;
			width: ${isOpen ? "657px" : "360px"};
			height: ${isOpen ? "720px" : "330px"};
			transition:
				width 0.5s ease,
				height 0.5s ease,
				border-color 0.1s ease;
			will-change: width, height;
			overflow: clip;
		`),

		...f.tablet(css`
			position: relative;
			padding: 20px;
			width: 100%;
			height: ${isOpen ? "720px" : "459px"};
			border: none;
		`),

		...f.mobile(css`
			position: relative;
			padding: 20px;
			width: 100%;
			height: ${isOpen ? "446px" : "237px"};
		`),
	}
})

const Testimonial = styled(
	UniversalLink,
	({ isOpen, color }: { isOpen?: boolean; color: string }) => {
		return {
			...f.unresponsive(css`
				border: 0.75px solid ${color};
				cursor: pointer;
			`),
			...f.responsive(css`
				width: ${isOpen ? "617px" : "320px"};
				height: ${isOpen ? "420px" : "290px"};
				border-radius: 20px;
				transition: all 0.5s ease;
				will-change: width, height;
				position: relative;
				display: flex;
				justify-content: center;
				overflow: clip;
			`),

			...f.tablet(css`
				width: 100%;
				height: 420px;
				border-radius: 20px;
			`),

			...f.mobile(css`
				width: 100%;
				height: 197px;
				border-radius: 20px;
			`),
		}
	},
)

const Quote = styled(
	"div",
	({ isOpen, color }: { isOpen?: boolean; color: string }) => {
		return {
			...f.responsive(css`
				position: absolute;
				bottom: ${isOpen ? "0" : "-100%"};
				left: 0;
				width: 100%;
				height: 260px;
				${textStyles.bodyXL};
				color: ${color};
				padding: 48px 40px;
				opacity: ${isOpen ? 1 : 0};
				transition: opacity 0.3s ease 0.3s;
			`),

			...f.mobile(css`
				bottom: ${isOpen ? "0" : "-100%"};
				width: 100%;
				height: 229px;
				${textStyles.bodyR};
				color: ${color};
				padding: 24px 12px 0;
				opacity: ${isOpen ? 1 : 0};
			`),
		}
	},
)

const QuoteInner = styled("div", {
	...f.responsive(css`
		position: absolute;
		bottom: 48px;
		left: 40px;
		width: 577px;
		${textStyles.bodyXL};
	`),

	...f.mobile(css`
		position: absolute;
		bottom: unset;
		top: 36px;
		left: 32px;
		width: 263px;
		${textStyles.bodyS};
	`),
})

const Chevron = styled(
	ChevronSVG,
	({
		isOpen,
		colors,
	}: {
		isOpen?: boolean
		colors: { primary: string; secondary: string }
	}) => {
		return {
			...f.responsive(css`
				transform: ${isOpen ? "rotate(180deg)" : "rotate(0deg)"};
				transition: transform 0.5s ease;
				width: 14px;
				height: 10px;
				position: absolute;
				top: 28px;
				right: 24px;

				path {
					stroke: ${colors.primary};
					fill: ${colors.secondary};
				}
			`),

			...f.mobile(css`
				display: none;
			`),
		}
	},
)

const TestimonialImage = styled(StaticImage, {
	...f.responsive(css`
		transition: all 0.5s ease;
		will-change: transform;
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		width: 100%;
		height: 100%;
	`),

	...f.tablet(css`
		position: absolute;
		right: 0;
	`),

	...f.mobile(css`
		right: 0;
		bottom: 0;
	`),
})

const ImageWrapper = styled("div", ({ isOpen }: { isOpen?: boolean }) => {
	return {
		...f.responsive(css`
			transition: all 0.5s ease;
			will-change: transform;
			position: absolute;
			right: 0;
			top: 0;
			width: auto;
			height: 100%;
			aspect-ratio: 320 / 289;
			min-width: 320px;
			transform-origin: top right;
			z-index: 0;
		`),

		...f.tablet(css`
			position: absolute;
			right: 0;
		`),

		...f.mobile(css`
			width: 194.5px;
			height: 170px;
			aspect-ratio: unset;
			min-width: unset;
			transform: scale(1);
			position: absolute;
			right: 0;
			top: unset;
			bottom: 0;
		`),
	}
})

// Uncomment the following lines if you want to add a company logo
// const CompanyLogo = styled(StaticImage, ({ isOpen }: { isOpen?: boolean }) => {
// 	return {
// 		...f.responsive(css`
// 			position: absolute;
// 			width: 63px;
// 			height: 14px;
// 			top: 24px;
// 			left: 22px;
// 			opacity: ${isOpen ? 1 : 0};
// 			transition: opacity 0.25s ease;
// 		`),

// 		...f.tablet(css`
// 			opacity: ${isOpen ? 1 : 0.1};
// 		`),

// 		...f.mobile(css`
// 			opacity: ${isOpen ? 1 : 0.1};
// 			width: 48px;
// 			height: 11px;
// 			top: 15px;
// 			left: 12px;
// 		`),
// 	}
// })

const Author = styled("div", ({ isOpen }: { isOpen?: boolean }) => {
	return {
		...f.responsive(css`
			display: flex;
			position: absolute;
			bottom: 24px;
			left: 22px;
			gap: 10px;
			align-items: center;
			opacity: ${isOpen ? 1 : 0};
			transition: opacity 0.25s ease;
			z-index: 1;
		`),

		...f.tablet(css`
			opacity: ${isOpen ? 1 : 0.1};
		`),

		...f.mobile(css`
			opacity: ${isOpen ? 1 : 0.1};
			left: 12px;
			bottom: 12px;
			gap: 0;
		`),
	}
})

const AuthorImage = styled(StaticImage, {
	...f.responsive(css`
		width: 32px;
		height: 32px;
	`),

	...f.mobile(css`
		display: none;
	`),
})

const AuthorInfo = styled("div", {
	...f.responsive(css`
		display: flex;
		flex-direction: column;
		gap: 10px;
	`),

	...f.mobile(css`
		gap: 6px;
	`),
})

const AuthorName = styled("p", ({ color }: { color: string }) => {
	return {
		...f.responsive(css`
			${textStyles.custom.bodyXSAlt};
			color: ${color};
		`),
	}
})

const JobTitle = styled("p", ({ color }: { color: string }) => {
	return {
		...f.responsive(css`
			${textStyles.titleS};
			color: ${color};
		`),
	}
})

const MarqueeWrapper = styled("div", {
	...f.unresponsive(css`
		border-top: 0.75px solid ${colors.green100};
	`),
	...f.responsive(css`
		width: 1519px;
		user-select: none;
		margin-top: -1px;
	`),

	...f.tablet(css`
		width: 100%;
	`),

	...f.mobile(css`
		width: 327px;
		overflow: clip;
	`),
})

const Marquee = styled(InfiniteSideScroll, {
	...f.responsive(css`
		padding: 30px 0;
	`),
})

const MarqueeItem = styled("div", {
	...f.responsive(css`
		display: flex;
		gap: 30px;
		align-items: center;
		margin-left: 30px;
	`),
})

const MarqueeText = styled("p", {
	...f.responsive(css`
		color: ${colors.green400};
		${textStyles.titleS};
	`),
})

const CardIcon = styled(CardSVG, {
	...f.responsive(css`
		width: 16px;
		height: 9px;

		.card-outline {
			stroke: ${colors.green100};
		}

		.card-detail {
			fill: ${colors.green100};
		}
	`),
})
